import { useEffect, useId, useRef } from 'react';
import Button from './Button';
import './ui.css';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * 取得 dialog 內可鍵盤聚焦的元素（排除隱藏／disabled）。
 */
function getFocusableElements(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((node) => {
    if (!(node instanceof HTMLElement)) return false;
    if (node.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return true;
  });
}

function safeFocus(element) {
  if (!(element instanceof HTMLElement)) return;
  if (!element.isConnected) return;
  try {
    element.focus();
  } catch {
    /* prior 元素可能已不可聚焦；忽略 */
  }
}

/**
 * 共用確認對話框。漸進取代 window.confirm。
 * focus capture／restore 只綁 open lifecycle；loading／onCancel 走 refs。
 * overlay 點擊不觸發 destructive confirm。
 */
export default function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  loading = false,
  onConfirm,
  onCancel,
}) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef(null);
  const cancelRef = useRef(null);
  const priorFocusedRef = useRef(null);
  const loadingRef = useRef(loading);
  const onCancelRef = useRef(onCancel);

  loadingRef.current = loading;
  onCancelRef.current = onCancel;

  // open lifecycle：capture／initial focus／keyboard／restore（不依賴 loading／onCancel）
  useEffect(() => {
    if (!open) return undefined;

    priorFocusedRef.current = document.activeElement;

    const focusInitial = () => {
      const cancel = cancelRef.current;
      if (cancel instanceof HTMLElement && !cancel.disabled) {
        safeFocus(cancel);
        return;
      }
      safeFocus(dialogRef.current);
    };

    // 等 dialog DOM 就緒後再聚焦
    focusInitial();

    function onKeyDown(event) {
      if (event.key === 'Escape') {
        if (!loadingRef.current) {
          event.preventDefault();
          onCancelRef.current?.();
        }
        return;
      }

      if (event.key !== 'Tab') return;

      const root = dialogRef.current;
      if (!root) return;

      const focusables = getFocusableElements(root);
      if (focusables.length === 0) {
        event.preventDefault();
        safeFocus(root);
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !root.contains(active)) {
          event.preventDefault();
          safeFocus(last);
        }
      } else if (active === last || !root.contains(active)) {
        event.preventDefault();
        safeFocus(first);
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const prior = priorFocusedRef.current;
      priorFocusedRef.current = null;
      if (prior instanceof HTMLElement && prior.isConnected) {
        safeFocus(prior);
      }
    };
  }, [open]);

  // loading 變化時：僅確保 focus 留在 dialog 內（不 restore 背景）
  useEffect(() => {
    if (!open) return;
    const root = dialogRef.current;
    if (!root) return;
    if (root.contains(document.activeElement)) return;
    const focusables = getFocusableElements(root);
    safeFocus(focusables[0] || root);
  }, [open, loading]);

  if (!open) return null;

  return (
    <div className="ui-dialog-backdrop" data-testid="ui-confirm-backdrop">
      <div
        ref={dialogRef}
        className="ui-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        data-testid="ui-confirm-dialog"
      >
        <h2 id={titleId} className="ui-dialog__title">
          {title}
        </h2>
        {description ? (
          <p id={descriptionId} className="ui-dialog__description">
            {description}
          </p>
        ) : null}
        <div className="ui-dialog__actions">
          <Button
            ref={cancelRef}
            variant="secondary"
            disabled={loading}
            onClick={onCancel}
            data-testid="ui-confirm-cancel"
          >
            {cancelLabel}
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            loading={loading}
            onClick={loading ? undefined : onConfirm}
            data-testid="ui-confirm-ok"
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
